import type { AstDocument, TemplateSummary, TemplateVersion, VariablesSchema } from '@emailforge/schemas';

import { apiSlice } from '@/features/api/apiSlice';

export type TrackingDefaults = { open: boolean; click: boolean };

export type EditorLock = {
  byUserId: string;
  byUserName: string | null;
  byUserEmail: string | null;
  heartbeatAt: string;
};

export type TemplateDetail = {
  id: string;
  name: string;
  slug: string;
  description: string | null;
  tags: string[];
  collectionId: string | null;
  status: 'draft' | 'published' | 'archived';
  defaultTracking: TrackingDefaults | null;
  latestVersion: TemplateVersion | null;
  lock: EditorLock | null;
  createdAt: string;
  updatedAt: string;
};

export type UpdateTemplateInput = {
  name?: string;
  description?: string | null;
  collectionId?: string | null;
  tags?: string[];
  defaultTracking?: TrackingDefaults | null;
};

export type GalleryItem = {
  id: string;
  slug: string;
  name: string;
  category: string;
  forkedCount: number;
  previewUrl: string | null;
};

export type TemplateVersionSummary = {
  id: string;
  versionNumber: number;
  isPublished: boolean;
  changeSummary: string | null;
  createdAt: string;
  createdBy: string | null;
  author: { id: string; name: string | null; email: string; avatarUrl: string | null } | null;
};

export type ImportDiagnostic = {
  severity: 'warning' | 'error';
  message: string;
  line?: number;
};

export type ImportTemplateResult = {
  id: string;
  slug: string;
  diagnostics: ImportDiagnostic[];
  blocksImported: number;
  rawJsxFallback: string | null;
};

export const templatesApi = apiSlice.injectEndpoints({
  endpoints: (b) => ({
    listTemplates: b.query<TemplateSummary[], void>({
      query: () => '/templates',
      providesTags: (result) =>
        result
          ? [...result.map((t) => ({ type: 'Template' as const, id: t.id })), { type: 'Template', id: 'LIST' }]
          : [{ type: 'Template', id: 'LIST' }],
    }),
    getTemplate: b.query<TemplateDetail, string>({
      query: (id) => `/templates/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Template', id }],
    }),
    createTemplate: b.mutation<{ id: string }, { name: string; collectionId?: string | null; forkFromGalleryId?: string }>(
      {
        query: (body) => ({ url: '/templates', method: 'POST', body }),
        invalidatesTags: [{ type: 'Template', id: 'LIST' }],
      },
    ),
    saveVersion: b.mutation<
      TemplateVersion,
      { templateId: string; astJson: AstDocument; variablesSchema: VariablesSchema; changeSummary?: string }
    >({
      query: ({ templateId, ...body }) => ({
        url: `/templates/${templateId}/versions`,
        method: 'PUT',
        body,
      }),
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: 'Template', id: templateId },
        { type: 'TemplateVersion', id: templateId },
      ],
    }),
    publishVersion: b.mutation<{ ok: true }, { templateId: string; versionId: string }>({
      query: ({ templateId, versionId }) => ({
        url: `/templates/${templateId}/publish`,
        method: 'POST',
        body: { versionId },
      }),
      invalidatesTags: (_result, _error, { templateId }) => [{ type: 'Template', id: templateId }],
    }),
    previewTemplate: b.mutation<{ html: string; warnings: string[] }, { templateId: string; variables?: Record<string, unknown> }>({
      query: ({ templateId, variables }) => ({
        url: `/templates/${templateId}/preview`,
        method: 'POST',
        body: { variables: variables ?? {} },
      }),
    }),
    updateTemplate: b.mutation<TemplateDetail, { id: string; patch: UpdateTemplateInput }>({
      query: ({ id, patch }) => ({ url: `/templates/${id}`, method: 'PATCH', body: patch }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Template', id },
        { type: 'Template', id: 'LIST' },
      ],
    }),
    deleteTemplate: b.mutation<void, string>({
      query: (id) => ({ url: `/templates/${id}`, method: 'DELETE' }),
      invalidatesTags: [{ type: 'Template', id: 'LIST' }],
    }),
    listGallery: b.query<GalleryItem[], void>({
      query: () => '/gallery/templates',
      providesTags: ['Gallery'],
    }),
    listVersions: b.query<TemplateVersionSummary[], string>({
      query: (templateId) => `/templates/${templateId}/versions`,
      providesTags: (_result, _error, id) => [{ type: 'TemplateVersion', id }],
    }),
    getVersion: b.query<TemplateVersion, { templateId: string; versionId: string }>({
      query: ({ templateId, versionId }) => `/templates/${templateId}/versions/${versionId}`,
      providesTags: (_result, _error, { versionId }) => [{ type: 'TemplateVersion', id: versionId }],
    }),
    restoreVersion: b.mutation<TemplateVersion, { templateId: string; versionId: string }>({
      query: ({ templateId, versionId }) => ({
        url: `/templates/${templateId}/versions/${versionId}/restore`,
        method: 'POST',
      }),
      invalidatesTags: (_result, _error, { templateId }) => [
        { type: 'Template', id: templateId },
        { type: 'TemplateVersion', id: templateId },
      ],
    }),
    importTemplate: b.mutation<
      ImportTemplateResult,
      { name: string; source: string; collectionId?: string | null }
    >({
      query: (body) => ({ url: '/templates/import', method: 'POST', body }),
      invalidatesTags: [{ type: 'Template', id: 'LIST' }],
    }),
    runPreflight: b.mutation<PreflightReport, string>({
      query: (templateId) => ({ url: `/templates/${templateId}/preflight`, method: 'POST' }),
    }),
    heartbeatTemplate: b.mutation<void, string>({
      query: (templateId) => ({ url: `/templates/${templateId}/heartbeat`, method: 'POST' }),
    }),
  }),
});

export type PreflightCheck = {
  id: string;
  category: 'image-weight' | 'spam-words' | 'variables';
  status: 'ok' | 'warn' | 'fail';
  title: string;
  message: string;
  details?: Record<string, unknown>;
};

export type PreflightReport = {
  templateId: string;
  versionId: string;
  ranAt: string;
  durationMs: number;
  summary: { ok: number; warn: number; fail: number };
  checks: PreflightCheck[];
};

export const {
  useListTemplatesQuery,
  useGetTemplateQuery,
  useCreateTemplateMutation,
  useSaveVersionMutation,
  usePublishVersionMutation,
  usePreviewTemplateMutation,
  useDeleteTemplateMutation,
  useListGalleryQuery,
  useListVersionsQuery,
  useGetVersionQuery,
  useRestoreVersionMutation,
  useImportTemplateMutation,
  useUpdateTemplateMutation,
  useRunPreflightMutation,
  useHeartbeatTemplateMutation,
} = templatesApi;
